"use client";

import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";

export default function UserDropdown() {
    const { user, logout, showToast, openProfileSettings } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const isAdmin = String(user?.role || "").toUpperCase() === "ADMIN";

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const displayName = user?.name || user?.email || user?.phone || "User";

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-center w-11 h-11 rounded-full border border-primary-light bg-primary/5 text-primary hover:bg-primary hover:text-white transition cursor-pointer focus:outline-none"
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-md border border-primary-light/50 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
                    <div className="px-4 py-3 border-b border-primary-light/50 bg-zinc-50 flex flex-col gap-0.5">
                        <p className="text-sm font-semibold text-zinc-800 truncate">{displayName}</p>
                        {(user?.email || user?.phone) && (
                            <p className="text-[11px] text-zinc-500 truncate">{user.email || user.phone}</p>
                        )}
                    </div>
                    <Link
                        href="/orders"
                        onClick={() => setIsOpen(false)}
                        className="w-full text-left px-4 py-3 text-sm text-zinc-700 font-semibold hover:bg-zinc-50 transition cursor-pointer flex items-center gap-2 group"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h12M3.75 3h16.5A.75.75 0 0 1 21 3.75V15a.75.75 0 0 1-.75.75H6a2.25 2.25 0 0 1-2.25-2.25m0 0V6.75A2.25 2.25 0 0 1 6 4.5h4.5" />
                        </svg>
                        History Pembelian
                    </Link>
                    {isAdmin && (
                        <Link
                            href="/admin/orders"
                            onClick={() => setIsOpen(false)}
                            className="w-full text-left px-4 py-3 text-sm text-zinc-700 font-semibold hover:bg-zinc-50 transition cursor-pointer flex items-center gap-2 group"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25v3A2.25 2.25 0 0 1 18.75 10.5H5.25A2.25 2.25 0 0 1 3 8.25v-3ZM3 15.75a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 15.75v3A2.25 2.25 0 0 1 18.75 21H5.25A2.25 2.25 0 0 1 3 18.75v-3Z" />
                            </svg>
                            Admin Orders
                        </Link>
                    )}
                    <button
                        onClick={() => {
                            openProfileSettings();
                            setIsOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-zinc-700 font-semibold hover:bg-zinc-50 transition cursor-pointer flex items-center gap-2 group"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0 1 13.191-5.007M4.5 12a7.5 7.5 0 0 0 13.191 5.007M4.5 12h15m-7.5-7.5v15" />
                        </svg>
                        Pengaturan
                    </button>
                    <button
                        onClick={() => {
                            logout();
                            showToast("Berhasil logout.");
                            setIsOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-red-600 font-semibold hover:bg-red-50 transition cursor-pointer flex items-center gap-2 group"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                        </svg>
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
}
